/**
 * CCM Handoff #2 §2.3 — client-side, deterministic case switching.
 *
 * The lawyer types a case name (or part of one); this hook resolves it
 * against the case list LexIDE already has (`useCases`, key `["cases"]`)
 * and returns a result the switch UI renders:
 *
 *   - `unique`    → exactly one match — caller may `switchToCase()`.
 *   - `ambiguous` → ≥2 matches — caller MUST show the candidates and
 *                   require a one-tap pick (the §10.6 disambiguation
 *                   guard — NEVER a silent guess on same-party collision
 *                   like "Silva v ABC" vs "Silva v Estado").
 *   - `none`      → zero matches.
 *
 * Resolution is **client-authoritative** and deterministic — never an
 * LLM decision (guide §3 / §7 invariant 3). Switching is the L1
 * session-key→transcript change: `selectCase()` re-points
 * `useActiveContextStore`, which drives `useDerivedChatSessionKey()`.
 *
 * OPTIONAL backend assist: when lexserver's `GET /api/lx/resolve-case`
 * (Handoff #1) is live, `resolveRemote()` prefers it (covers cases
 * beyond the loaded list + the authoritative disambiguation set) and
 * falls back to the local match when the endpoint is absent — so the
 * UI works **today** and upgrades transparently. The synchronous
 * `resolveLocal()` is always available for instant, type-ahead UX.
 */

import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCases } from "@/hooks/useCases";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import {
  resolveCaseRemote,
  type CaseListItem,
  type ResolveCaseCandidate,
} from "@/lib/lexserver-client";

export interface CaseSwitchCandidate {
  caseId: string;
  clientId?: string;
  matterId?: string;
  title: string;
  status?: string;
}

export type CaseSwitchKind = "unique" | "ambiguous" | "none";

export interface CaseSwitchResolution {
  kind: CaseSwitchKind;
  candidates: CaseSwitchCandidate[];
  /** True when the result came from the lexserver resolver (Handoff #1);
   *  false when it came from the local `useCases` list. Surfaced so the
   *  UI / tests can tell which path answered. */
  fromRemote: boolean;
}

/** Normalize for accent-insensitive, case-insensitive substring match. */
function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function toCandidate(c: CaseListItem): CaseSwitchCandidate {
  return {
    caseId: c.caseId,
    // Parent ids are now tri-state (string | null | undefined); this candidate
    // shape models "unassigned" as undefined, so fold an explicit null into it.
    clientId: c.clientId ?? undefined,
    matterId: c.matterId ?? undefined,
    title: c.title,
    status: c.status,
  };
}

function remoteToCandidate(c: ResolveCaseCandidate): CaseSwitchCandidate {
  return {
    caseId: c.caseId,
    clientId: c.clientId,
    matterId: c.matterId,
    title: c.title,
    status: c.status,
  };
}

/**
 * Match a query against the loaded case list. Matches on title and
 * caseId (the parties/number live inside the title for now). Returns
 * the dedup'd, capped candidate set. An exact (normalized) title match
 * collapses to a single `unique` even when a broader substring set
 * exists — typing the full name shouldn't be drowned by near-misses.
 */
export function matchCasesLocally(
  cases: CaseListItem[],
  query: string,
  cap = 8,
): CaseSwitchResolution {
  const q = normalize(query);
  if (!q) return { kind: "none", candidates: [], fromRemote: false };

  const exact = cases.filter((c) => normalize(c.title) === q);
  if (exact.length === 1) {
    return { kind: "unique", candidates: [toCandidate(exact[0])], fromRemote: false };
  }

  const substring = cases.filter(
    (c) => normalize(c.title).includes(q) || normalize(c.caseId).includes(q),
  );
  const matches = (exact.length > 1 ? exact : substring).slice(0, cap);

  if (matches.length === 0) return { kind: "none", candidates: [], fromRemote: false };
  if (matches.length === 1) {
    return { kind: "unique", candidates: matches.map(toCandidate), fromRemote: false };
  }
  return { kind: "ambiguous", candidates: matches.map(toCandidate), fromRemote: false };
}

/** "/global", "global", "geral", "exit", "sair", "voltar" all mean
 *  "leave the case, go to the per-user org scope". */
const GLOBAL_VERBS = new Set([
  "/global",
  "global",
  "geral",
  "/geral",
  "exit",
  "sair",
  "voltar",
  "salir",
]);

export function isGlobalVerb(query: string): boolean {
  return GLOBAL_VERBS.has(normalize(query));
}

export interface UseCaseSwitcher {
  /** Loaded? Drives the picker's "loading cases…" state. */
  isLoading: boolean;
  /** Synchronous local resolution — instant type-ahead. */
  resolveLocal: (query: string) => CaseSwitchResolution;
  /** Local-first with optional lexserver assist. Falls back to local
   *  when the endpoint is absent. Never throws. */
  resolveRemote: (query: string) => Promise<CaseSwitchResolution>;
  /** Whether the query is the "go to global/org scope" verb. */
  isGlobalVerb: (query: string) => boolean;
  /** Re-select a case (L1 switch) + navigate into its workspace.
   *  Returns `false` (a no-op) when the candidate lacks the parent ids
   *  required to build the canonical session key — callers should keep
   *  the picker open rather than silently dead-ending. */
  switchToCase: (c: CaseSwitchCandidate) => boolean;
  /** Clear the selection (→ per-user org/global scope) + go /home. */
  goGlobal: () => void;
}

export function useCaseSwitcher(options: { enabled?: boolean } = {}): UseCaseSwitcher {
  const { data: cases, isLoading } = useCases({ enabled: options.enabled ?? true });
  const navigate = useNavigate();
  const selectCase = useActiveContextStore((s) => s.selectCase);
  const clear = useActiveContextStore((s) => s.clear);

  const caseList = useMemo(() => cases ?? [], [cases]);

  const resolveLocal = useCallback(
    (query: string): CaseSwitchResolution => matchCasesLocally(caseList, query),
    [caseList],
  );

  const resolveRemote = useCallback(
    async (query: string): Promise<CaseSwitchResolution> => {
      const remote = await resolveCaseRemote(query);
      if (remote) {
        return {
          kind: remote.kind,
          candidates: remote.candidates.map(remoteToCandidate),
          fromRemote: true,
        };
      }
      return matchCasesLocally(caseList, query);
    },
    [caseList],
  );

  const switchToCase = useCallback(
    (c: CaseSwitchCandidate): boolean => {
      // N1: navigate the SHORT canonical (/cases/:id) — the long URL is no
      // longer generated anywhere. This also UN-BAILS unassigned cases: the
      // old guard refused null parents ("can't build a case-scope key"), but
      // that predates D-8 — CaseRouteResolver + useCaseWorkspace open by
      // caseId alone, and OverviewNav's case rows already switch this way.
      // `null` (not "") so downstream `?? "Unassigned"` fallbacks fire (L1).
      selectCase(c.clientId ?? null, c.matterId ?? null, c.caseId);
      navigate(`/cases/${c.caseId}`);
      return true;
    },
    [selectCase, navigate],
  );

  const goGlobal = useCallback(() => {
    clear();
    navigate("/home");
  }, [clear, navigate]);

  return {
    isLoading,
    resolveLocal,
    resolveRemote,
    isGlobalVerb,
    switchToCase,
    goGlobal,
  };
}
